import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { uniq } from 'lodash-es';
import { v4 } from 'uuid';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ReportPeriodDto } from '@src/api';

export type ReportPeriodDtoDefId = ReportPeriodDto & { id: string };

export type Filters = {
  reportGroup?: string;
};

class ReportPeriodsStore {
  reportPeriods: ReportPeriodDtoDefId[] = [];

  api: CmApi;

  filters: Filters = {};

  breadcrumbs: string[] = [];

  fullSize = false;

  selectNode: ReportPeriodDtoDefId | null = null;

  selectedKeys: string[] = [];

  expandedKeys: string[] = [];

  checked: ResponseDataRow[] = [];

  isChanged = false; // флаг, что справочник изменился для проверки загрузки в fetchReferences

  groupInPeriods: (string | undefined)[] = [];

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setIsChanged = (value: boolean) => {
    this.isChanged = value;
  };

  setFilters = (filter: Filters) => {
    this.filters = filter;
  };

  setBreadcrumbs = (value: string[]) => {
    this.breadcrumbs = value;
  };

  toggleFullSize = () => {
    this.fullSize = !this.fullSize;
  };

  setSelectNode = (value: ReportPeriodDtoDefId | null) => {
    this.selectNode = value;

    if (value === null) {
      this.fullSize = false;
    }
  };

  setSelectedKeys = (value: string[]) => {
    this.selectedKeys = value;
  };

  setExpandedKeys = (value: string[]) => {
    this.expandedKeys = value;
  };

  setChecked = (value) => {
    this.checked = value;
  };

  setGroupInPeriods = (value: (string | undefined)[]) => {
    this.groupInPeriods = value;
  };

  fetch = async (): Promise<void> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.reportPeriods.getReportPeriods();

    runInAction(() => {
      this.reportPeriods = data?.map((item) => ({ ...item, id: v4() })) ?? [];
      this.isChanged = false;
    });
    preloaderStore.preloader(false);
  };

  groupDelete = async (periods: ReportPeriodDtoDefId[]) => {
    preloaderStore.preloader(true, 'удаление...');
    await this.api.reportPeriods.deleteReportPeriods(periods);
    await this.fetch();
    preloaderStore.preloader(false);
  };

  clear = () => {
    this.filters = {};
    this.breadcrumbs = [];
    this.fullSize = false;
    this.selectNode = null;
    this.selectedKeys = [];
    this.expandedKeys = [];
    this.checked = [];
  };
}

export const reportPeriodsStore = new ReportPeriodsStore({ api: cmApi });

reaction(
  () => reportPeriodsStore.reportPeriods,
  (periods) => {
    const groups = uniq(periods.map((item) => item.reportGroupCode).filter((item) => !!item));
    reportPeriodsStore.setGroupInPeriods(groups);
  },
);
